import { Button, Chip, Skeleton, Stack, Typography } from "@mui/material";
import { format, parseISO, startOfWeek } from "date-fns";
import { StyledCard, BoZoneDivider, EmptyStateCard } from "../../layer01";
import { CompletedCheckInSummary } from "app/containers/TeamsTab/types";
import { ConversationStarterList } from "../../layer07";
import { useNavigate, useStartChatAboutTopic } from "../../../common";
import messagesIllustration from "resources/images/messages-illustration.png";

type BoZoneWeekCardProps = {
  checkInSummary?: CompletedCheckInSummary | null;
  isCheckInSummaryLoading?: boolean;
  hasPendingCheckIn?: boolean;
};

type WorkItem = {
  title: string;
  description: string;
};

const ListSkeleton = ({ widths }: { widths: string[] }) => (
  <Stack direction="column" rowGap=".25rem">
    {widths.map((width) => (
      <Skeleton key={width} width={width} height={16} />
    ))}
  </Stack>
);

const SectionSkeleton = () => (
  <Stack
    direction="column"
    rowGap=".5rem"
    flex={{
      xs: "0 0 auto",
      sm: "1 1 0",
    }}
    width={{
      xs: "100%",
      sm: "auto",
    }}
    minWidth={0}
  >
    <Skeleton width="35%" height={22} />
    <ListSkeleton widths={["90%", "70%"]} />
  </Stack>
);

const getWeekLabel = (dateStr?: string) => {
  if (!dateStr) return null;
  // parseISO reads a date-only string as local midnight; new Date() reads it
  // as UTC, which lands on the previous day in US time zones.
  const date = parseISO(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  return `Week of ${format(startOfWeek(date, { weekStartsOn: 1 }), "MMM d")}`;
};

export const BoZoneWeekCard = ({
  checkInSummary,
  isCheckInSummaryLoading,
  hasPendingCheckIn,
}: BoZoneWeekCardProps) => {
  const navigate = useNavigate();
  const { startChatAboutTopic, pendingTopic } = useStartChatAboutTopic();
  const showConversationStarters =
    !!checkInSummary?.conversationsStarted?.length;
  const weekLabel = getWeekLabel(
    checkInSummary?.checkInScheduledDate ?? checkInSummary?.checkInCompletedDate
  );
  const workItems: WorkItem[] = [
    {
      title: "Summary",
      description:
        checkInSummary?.currentWeekWork ?? checkInSummary?.summary ?? "",
    },
    {
      title: "Themes Identified",
      description:
        checkInSummary?.secondaryTheme ??
        checkInSummary?.primaryTheme ??
        (checkInSummary ? "No theme identified." : ""),
    },
  ].filter((item) => item.description);
  const hasWinsOrSupport =
    !!checkInSummary?.winSpotlight || !!checkInSummary?.supportNeeds;
  const isEmpty =
    !isCheckInSummaryLoading &&
    !workItems.length &&
    !hasWinsOrSupport &&
    !showConversationStarters;

  const renderListItem = ({ title, description }: WorkItem) => (
    <Typography
      key={title}
      component="li"
      variant="cardBodyRegular"
      color="#53565A"
    >
      <strong>{title}:</strong> {description}
    </Typography>
  );

  const getStackSection = (title: string, bulletPoints: string[]) => (
    <Stack
      key={title}
      direction="column"
      rowGap=".5rem"
      flex={{
        xs: "0 0 auto",
        sm: "1 1 0",
      }}
      width={{
        xs: "100%",
        sm: "auto",
      }}
      minWidth={0}
    >
      <Typography variant="cardSectionTitle">{title}</Typography>
      <Stack
        component="ul"
        direction="column"
        rowGap=".25rem"
        margin={0}
        paddingLeft="1.125rem"
      >
        {bulletPoints.map((point) => (
          <Typography
            key={point}
            component="li"
            variant="cardBodyRegular"
            color="#53565A"
          >
            {point}
          </Typography>
        ))}
      </Stack>
    </Stack>
  );

  const getBadge = () => {
    if (isCheckInSummaryLoading) {
      return <Skeleton variant="rounded" width="6.5rem" height="1.5rem" />;
    }
    return weekLabel ? <Chip variant="info" label={weekLabel} /> : undefined;
  };

  const getEmptyState = () => (
    <EmptyStateCard
      image={messagesIllustration}
      title="Nothing to recap yet"
      body={
        hasPendingCheckIn
          ? "Complete a check-in to see what you worked on, your wins, and where you need support."
          : "After your next check-in, you'll see what you worked on, your wins, and where you need support."
      }
      action={
        hasPendingCheckIn ? (
          <Button variant="outlined" onClick={() => navigate("/user/checkIn")}>
            Start a check-in
          </Button>
        ) : undefined
      }
    />
  );

  const getWorkSection = () => {
    if (!isCheckInSummaryLoading && !workItems.length) return null;
    return (
      <Stack direction="column" rowGap="1rem">
        <Typography variant="modalTitle">What you worked on</Typography>
        {isCheckInSummaryLoading ? (
          <ListSkeleton widths={["95%", "85%", "60%"]} />
        ) : (
          <Stack
            component="ul"
            direction="column"
            rowGap=".25rem"
            margin={0}
            paddingLeft="1.125rem"
          >
            {workItems.map(renderListItem)}
          </Stack>
        )}
      </Stack>
    );
  };

  const getWinsAndSupport = () => {
    if (isCheckInSummaryLoading) {
      return (
        <>
          <SectionSkeleton />
          <SectionSkeleton />
        </>
      );
    }
    return (
      <>
        {checkInSummary?.winSpotlight
          ? getStackSection("Wins", [
              checkInSummary.winSpotlight.achievement,
              checkInSummary.winSpotlight.impact,
            ])
          : null}
        {checkInSummary?.supportNeeds
          ? getStackSection("Support Needed", [checkInSummary.supportNeeds])
          : null}
      </>
    );
  };

  const getWinsAndSupportSection = () => {
    if (!isCheckInSummaryLoading && !hasWinsOrSupport) return null;
    return (
      <>
        {(isCheckInSummaryLoading || !!workItems.length) && <BoZoneDivider />}
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          columnGap="1rem"
          rowGap="1rem"
          alignItems="flex-start"
        >
          {getWinsAndSupport()}
        </Stack>
      </>
    );
  };

  const getConversationStartersSection = () => {
    if (!showConversationStarters) return null;
    return (
      <>
        {(!!workItems.length || hasWinsOrSupport) && <BoZoneDivider />}
        <Stack direction="column" rowGap=".75rem">
          <Typography variant="cardSectionTitle">
            Conversation Starters
          </Typography>
          <ConversationStarterList
            checkInSummary={checkInSummary}
            action={{
              label: "Let's talk",
              onClick: startChatAboutTopic,
              pendingStarter: pendingTopic,
            }}
          />
        </Stack>
      </>
    );
  };

  return (
    <StyledCard title="This week" badge={getBadge()}>
      {isEmpty ? (
        getEmptyState()
      ) : (
        <Stack direction="column" rowGap="1rem">
          {getWorkSection()}
          {getWinsAndSupportSection()}
          {getConversationStartersSection()}
        </Stack>
      )}
    </StyledCard>
  );
};
